import React, { useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import AktorDetaljer, { getAktorDetailsFields } from './aktor-detaljer';
import ahvContext from '../../../ahv-context';
import { emptyList } from '../../../constants';
import { answersToActor, isValidAktor } from './validation';
import { BARE_LES_NAR_VALGT } from '../../../constants/general';
import CountryContext from '../../../country-context';

function AktorQuestionType({ question }) {
    const context = useContext(ahvContext);
    const countryContext = useContext(CountryContext);
    const { getHasSubmitted } = context;
    const [prefilledAktor, setPrefilledAktor] = useState(false);
    const { utfyllingsregel, config = {} } = question;

    const getShowErrors = useCallback(
        () => getHasSubmitted() && utfyllingsregel.regel !== 'FRIVILLIG',
        [getHasSubmitted, utfyllingsregel.regel]
    );

    const getInvalidList = useCallback(
        (answer, config) => {
            const keys = Object.keys(getAktorDetailsFields());
            const actor = answersToActor(answer);
            return keys.filter(
                (key) => !isValidAktor(key, actor, config, countryContext.getNorwegianPostalCodesOptions)
            );
        },
        [countryContext.getNorwegianPostalCodesOptions]
    );

    const addPrefilled = useCallback(
        (aktorString) => {
            const aktor = JSON.parse(aktorString);
            if (aktor.aktorId) {
                aktor.fnr = aktor.aktorId.value;
                if (utfyllingsregel.regel === BARE_LES_NAR_VALGT && prefilledAktor === false) {
                    setPrefilledAktor(aktor);
                }
            }

            return {
                aktorKey: uuid(),
                ...getAktorDetailsFields(),
                ...aktor,
            };
        },
        [utfyllingsregel, prefilledAktor, setPrefilledAktor]
    );

    const updateAnswer = useCallback(
        (newAns) => {
            const currentValue = context.getValue(question.definisjon);
            const value = { ...currentValue, ...newAns };
            const hasErrors = getShowErrors() ? getInvalidList(value, config).length > 0 : false;
            context.onChange(question.definisjon, { value, error: hasErrors });
        },
        [context, config, getShowErrors, question.definisjon, getInvalidList]
    );

    let subquestion = context.getValue(question.definisjon);
    if (typeof subquestion === 'string') {
        subquestion = addPrefilled(subquestion);
        context.onChange(question.definisjon, { value: subquestion });
    } else if (subquestion === emptyList) {
        subquestion = {};
    }

    return (
        <div className="aktor-skjema">
            <AktorDetaljer
                context={context}
                question={subquestion}
                onChange={updateAnswer}
                errors={getShowErrors() ? getInvalidList(subquestion, config) : emptyList}
                tekster={question.ekstratekster}
                prefilledAktor={prefilledAktor}
                config={config}
            />
        </div>
    );
}

AktorQuestionType.propTypes = {
    question: PropTypes.shape({
        definisjon: PropTypes.string.isRequired,
        utfyllingsregel: PropTypes.shape({
            regel: PropTypes.string.isRequired,
        }).isRequired,
        ekstratekster: PropTypes.shape({
            aktor_navn: PropTypes.string,
            aktor_fnr: PropTypes.string,
            aktor_fodeland: PropTypes.string,
            aktor_statsborgerskap: PropTypes.string,
            aktor_sekundart_statsborgerskap: PropTypes.string,
            aktor_adresse: PropTypes.string,
            aktor_postnummer: PropTypes.string,
            aktor_poststed: PropTypes.string,
            aktor_bostedsland: PropTypes.string,
        }),
        config: PropTypes.shape({
            addressInformationOptional: PropTypes.bool,
        }),
    }).isRequired,
};

export default AktorQuestionType;
